import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/shared/lib/cn'

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

/**
 * Las cuatro variantes, TODAS con borde propio.
 *
 * El borde no es decoracion: es lo que hace que un control se lea como control
 * antes de que nadie lo toque. `ghost` lo lleva transparente y lo revela en
 * hover, asi no salta de tamano al pasar el mouse.
 *
 * Se usa `border border-<token>` y no la clase `.hairline`: `.hairline` vive
 * fuera de capa —le gana a las utilidades a proposito— y por eso ningun
 * `hover:border-*` podria pisarla. Con utilidades de las dos puntas, el hover
 * cambia el borde de verdad.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-signal text-on-signal border border-signal font-medium hover:brightness-110 active:brightness-95',
  outline:
    'bg-surface-1 text-text-1 border border-hairline hover:bg-surface-2 hover:border-text-3 active:bg-surface-2',
  ghost:
    'text-text-2 border border-transparent hover:bg-surface-2 hover:text-text-1 hover:border-hairline',
  danger:
    'bg-surface-1 text-overdue border border-hairline hover:bg-surface-2 hover:border-overdue active:bg-surface-2',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-6 px-2 text-2xs gap-1',
  md: 'h-7 px-3 text-xs gap-1.5',
}

/** Sin sombras: la jerarquia es superficie + borde + color de accion. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[var(--radius-control)] whitespace-nowrap',
        'transition-colors duration-[var(--motion-fast)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...props}
    />
  )
})
